import { useState } from 'react';
import type React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Contact,
  Package,
  ShoppingCart,
  CreditCard,
  Wallet,
  FileText,
  HelpCircle,
  MapPin,
  Users,
  ClipboardList,
  Settings,
  ShieldCheck,
  Globe,
  UserCheck,
  Image,
  Award,
  Menu,
  X,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import * as authService from '@/services/authService';
import { useResponsive } from '@/hooks/useResponsive';
import { useI18n } from '@/hooks/useI18n';
import { SiteLogo } from '@/components/common/SiteLogo';
import { AdminGlobalSearch } from '@/components/admin/AdminGlobalSearch';
import { cn } from '@/lib/utils';
import { ADMIN_PAGE_PERMISSIONS, hasAdminPermission } from '@/utils/adminPermissions';

interface NavItem {
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  labelKey: string;
  exact?: boolean;
  /** Permission needed to see the item; matches the route's gate. */
  permission: string;
}

interface NavSection {
  title: string;
  titleKey: string;
  items: NavItem[];
}

const P = ADMIN_PAGE_PERMISSIONS;

/** Admin sidebar navigation items */
const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Overview',
    titleKey: 'admin.nav.section.overview',
    items: [
      { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', labelKey: 'admin.nav.dashboard', exact: true, permission: P.dashboard },
    ],
  },
  {
    title: 'Business',
    titleKey: 'admin.nav.section.business',
    items: [
      { path: '/admin/customers', icon: Contact, label: 'Customers', labelKey: 'admin.customers.nav', permission: P.customers },
      { path: '/admin/packages', icon: Package, label: 'Packages', labelKey: 'admin.nav.packages', permission: P.packages },
      { path: '/admin/orders', icon: ShoppingCart, label: 'Orders', labelKey: 'admin.nav.orders', permission: P.orders },
      { path: '/admin/payments', icon: CreditCard, label: 'Payments', labelKey: 'admin.payments', permission: P.payments },
      { path: '/admin/wallets', icon: Wallet, label: 'Wallets', labelKey: 'admin.nav.wallets', permission: P.wallets },
    ],
  },
  {
    title: 'Content',
    titleKey: 'admin.nav.section.content',
    items: [
      { path: '/admin/articles', icon: FileText, label: 'Articles', labelKey: 'admin.nav.articles', permission: P.content },
      { path: '/admin/faq', icon: HelpCircle, label: 'FAQ', labelKey: 'admin.nav.faq', permission: P.content },
    ],
  },
  {
    title: 'Data',
    titleKey: 'admin.nav.section.data',
    items: [
      { path: '/admin/countries', icon: MapPin, label: 'Countries', labelKey: 'admin.nav.countries', permission: P.content },
      { path: '/admin/destinations', icon: Globe, label: 'Destinations', labelKey: 'admin.nav.destinations', permission: P.content },
      { path: '/admin/traveler-types', icon: UserCheck, label: 'Traveler Types', labelKey: 'admin.nav.travelerTypes', permission: P.content },
      { path: '/admin/membership-tiers', icon: Award, label: 'Membership Tiers', labelKey: 'admin.nav.membershipTiers', permission: P.membershipTiers },
    ],
  },
  {
    title: 'System',
    titleKey: 'admin.nav.section.system',
    items: [
      { path: '/admin/admins', icon: Users, label: 'Admin Users', labelKey: 'admin.nav.adminUsers', permission: P.admins },
      { path: '/admin/roles', icon: ShieldCheck, label: 'Roles & Permissions', labelKey: 'admin.nav.roles', permission: P.roles },
      { path: '/admin/audit-logs', icon: ClipboardList, label: 'Audit Logs', labelKey: 'admin.nav.auditLogs', permission: P.auditLogs },
      { path: '/admin/config', icon: Settings, label: 'System Config', labelKey: 'admin.nav.systemConfig', permission: P.config },
      { path: '/admin/files', icon: Image, label: 'Files', labelKey: 'admin.nav.files', permission: P.content },
    ],
  },
];

export default function AdminLayout() {
  const { t } = useI18n();
  // Fall back to the English label while translations load (or if a key is
  // missing), instead of showing the raw key.
  const tr = (key: string, fallback: string) => {
    const text = t(key);
    return text === key ? fallback : text;
  };
  const { md } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const admin = useAuthStore((state) => state.admin);
  const adminPermissions = useAuthStore((state) => state.adminPermissions);
  const clearAdminAuth = useAuthStore((state) => state.clearAdminAuth);

  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    // Clear the admin refresh cookie server-side so a reload can't restore
    // the session; clear local state regardless of the request outcome.
    // A customer session in the same browser stays signed in.
    try {
      await authService.adminLogout();
    } catch {
      // ignore — fall through to local cleanup
    }
    clearAdminAuth();
    navigate('/admin/login');
  };

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Only show what this admin can open; empty sections are dropped.
  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => hasAdminPermission(admin, adminPermissions, item.permission)),
  })).filter((section) => section.items.length > 0);

  const sidebarContent = (
    <nav className="flex flex-col gap-1 px-2 py-2">
      {visibleSections.map((section) => (
        <div key={section.titleKey} className="mb-2">
          {!sidebarCollapsed && (
            <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {tr(section.titleKey, section.title)}
            </p>
          )}
          {section.items.map((item) => {
            const Icon = item.icon;
            const label = tr(item.labelKey, item.label);
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => {
                  if (!md) setDrawerOpen(false);
                }}
                className={cn(
                  'flex min-h-[36px] items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  active
                    ? 'bg-primary/10 font-medium text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  sidebarCollapsed && 'justify-center px-2',
                )}
                title={sidebarCollapsed ? label : undefined}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {!sidebarCollapsed && <span>{label}</span>}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* ===== Desktop Sidebar ===== */}
      {md && (
        <aside
          className={cn(
            'fixed left-0 top-0 z-40 flex h-screen flex-col border-r bg-white transition-all duration-300',
            sidebarCollapsed ? 'w-16' : 'w-60',
          )}
        >
          {/* Sidebar Header */}
          <div className="flex h-14 items-center justify-between border-b px-3">
            {!sidebarCollapsed && (
              <Link
                to="/admin"
                className="flex items-center gap-2 text-lg font-bold text-primary"
              >
                <SiteLogo imageClassName="h-7" fallback="GoLoka" />
                <span className="text-xs font-semibold text-muted-foreground">Admin</span>
              </Link>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="h-8 w-8"
              aria-label={sidebarCollapsed ? tr('admin.nav.expandSidebar', 'Expand sidebar') : tr('admin.nav.collapseSidebar', 'Collapse sidebar')}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Sidebar Navigation */}
          <div className="flex-1 overflow-y-auto">{sidebarContent}</div>
        </aside>
      )}

      {/* ===== Mobile Drawer Overlay ===== */}
      {!md && drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="fixed left-0 top-0 z-50 flex h-screen w-64 flex-col border-r bg-white shadow-xl">
            <div className="flex h-14 items-center justify-between border-b px-3">
              <Link
                to="/admin"
                className="flex items-center gap-2 text-lg font-bold text-primary"
                onClick={() => setDrawerOpen(false)}
              >
                <SiteLogo imageClassName="h-7" fallback="GoLoka" />
                <span className="text-xs font-semibold text-muted-foreground">Admin</span>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDrawerOpen(false)}
                className="h-8 w-8"
                aria-label={tr('admin.nav.closeMenu', 'Close menu')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto">{sidebarContent}</div>
          </aside>
        </>
      )}

      {/* ===== Main Area ===== */}
      <div
        className={cn(
          'flex flex-1 flex-col transition-all duration-300',
          md && (sidebarCollapsed ? 'ml-16' : 'ml-60'),
        )}
      >
        {/* Top Header */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-4 border-b bg-white px-4 shadow-sm">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {/* Mobile hamburger */}
            {!md && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDrawerOpen(true)}
                className="h-9 w-9"
                aria-label={tr('admin.nav.openMenu', 'Open menu')}
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <AdminGlobalSearch />
          </div>

          <div className="flex items-center gap-3">
            {/* Admin info */}
            <div className="text-right">
              <p className="text-sm font-medium">
                {admin?.nickname || 'Admin'}
              </p>
              <p className="text-xs text-muted-foreground">{tr('admin.nav.administrator', 'Administrator')}</p>
            </div>

            {/* Logout */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="h-9 w-9 text-muted-foreground hover:text-destructive"
              aria-label={tr('admin.nav.logout', 'Logout')}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
